import { useMutation, useQueryClient } from "@tanstack/react-query";
import React from "react";
import { auth } from "../../../../../core/auth";
import useApi from "../../../../../shared/hooks/useApi";
import useEntityProfileToast from "../../../../entityProfileModal/hooks/useEntityProfileToast";
import type { PatientProfilePatient } from "../../../patientProfileModal.types";
import type { AddressDraft, PatientAddress } from "./addresses.types";
import { emptyAddressDraft, getAddressDraftKey, normalizeAddressDraft } from "./addresses.utils";

export function usePatientProfileAddressesController(patient: PatientProfilePatient) {
  const { api } = useApi();
  const queryClient = useQueryClient();
  const toast = useEntityProfileToast();
  const canEdit = auth.isAllowedTo("edit_patient_page_summary_details");
  const [draftAddresses, setDraftAddresses] = React.useState<AddressDraft[]>(patient.addresses);
  const [newAddress, setNewAddress] = React.useState<AddressDraft>(emptyAddressDraft);
  const [isEditing, setIsEditing] = React.useState(false);
  const [isAdding, setIsAdding] = React.useState(false);
  const [submittedAddressKeys, setSubmittedAddressKeys] = React.useState<Set<string>>(new Set());

  React.useEffect(() => {
    setDraftAddresses(patient.addresses);
  }, [patient.addresses]);

  const updatePatientAddressesCache = (addresses: PatientAddress[]) => {
    queryClient.setQueryData(["patient", "get", patient.id], (currentPatient: unknown) => {
      if (
        currentPatient === undefined ||
        currentPatient === null ||
        typeof currentPatient !== "object"
      ) {
        return currentPatient;
      }

      return { ...currentPatient, addresses };
    });
  };

  const saveAddressMutation = useMutation({
    mutationFn: (draft: AddressDraft) =>
      draft.id === undefined
        ? api.post("./patients/:patientId/addresses", {
            path: { patientId: patient.id },
            body: normalizeAddressDraft(draft),
          })
        : api.put("./patients/:patientId/addresses/:patientAddressId", {
            path: { patientId: patient.id, patientAddressId: draft.id },
            body: normalizeAddressDraft(draft),
          }),
    onSuccess: (response, draft) => {
      const nextAddresses =
        draft.id === undefined
          ? [...patient.addresses, response.address]
          : patient.addresses.map((address) =>
              address.id === response.address.id ? response.address : address
            );

      updatePatientAddressesCache(nextAddresses);
      setIsAdding(false);
      setNewAddress(emptyAddressDraft);
      setSubmittedAddressKeys((currentKeys) => {
        const nextKeys = new Set(currentKeys);
        nextKeys.delete(getAddressDraftKey(draft));
        return nextKeys;
      });
      toast({
        description: "Address saved successfully.",
        status: "success",
      });
    },
    onError: () => {
      toast({ description: "Could not save address.", status: "error" });
    },
  });

  const deleteAddressMutation = useMutation({
    mutationFn: (address: PatientAddress) =>
      api.delete("./patients/:patientId/addresses/:patientAddressId", {
        path: { patientId: patient.id, patientAddressId: address.id },
      }),
    onSuccess: (_, address) => {
      updatePatientAddressesCache(
        patient.addresses.filter((patientAddress) => patientAddress.id !== address.id)
      );
      toast({
        description: "Address deleted successfully.",
        status: "success",
      });
    },
    onError: () => {
      toast({ description: "Could not delete address.", status: "error" });
    },
  });

  const handleDeleteAddress = (address: PatientAddress) => {
    const shouldDelete = window.confirm("Are you sure you want to delete this address?");

    if (!shouldDelete) {
      return;
    }

    deleteAddressMutation.mutate(address);
  };

  const updateDraftAddress = (index: number, address: AddressDraft) => {
    setDraftAddresses((currentAddresses) =>
      currentAddresses.map((currentAddress, currentIndex) =>
        currentIndex === index ? address : currentAddress
      )
    );
  };
  const handleSaveAddress = (draft: AddressDraft) => {
    setSubmittedAddressKeys((currentKeys) => new Set(currentKeys).add(getAddressDraftKey(draft)));

    if (draft.address.trim() === "") {
      toast({
        description: "Address is required.",
        status: "error",
      });
      return;
    }

    saveAddressMutation.mutate(draft);
  };
  const wasDraftSubmitted = (draft: AddressDraft) =>
    submittedAddressKeys.has(getAddressDraftKey(draft));
  const clearNewAddressValidation = () => {
    setSubmittedAddressKeys((currentKeys) => {
      const nextKeys = new Set(currentKeys);
      nextKeys.delete(getAddressDraftKey(emptyAddressDraft));
      return nextKeys;
    });
  };
  const startAdding = () => {
    clearNewAddressValidation();
    setNewAddress(emptyAddressDraft);
    setIsAdding(true);
  };
  const cancelAdding = () => {
    clearNewAddressValidation();
    setIsAdding(false);
  };
  const toggleEditMode = () => {
    if (isEditing) {
      setDraftAddresses(patient.addresses);
      setNewAddress(emptyAddressDraft);
      setIsAdding(false);
      setSubmittedAddressKeys(new Set());
      setIsEditing(false);
      return;
    }

    setDraftAddresses(patient.addresses);
    setIsEditing(true);
  };

  return {
    canEdit,
    cancelAdding,
    deleteAddressMutation,
    draftAddresses,
    handleDeleteAddress,
    handleSaveAddress,
    isAdding,
    isEditing,
    newAddress,
    saveAddressMutation,
    setNewAddress,
    startAdding,
    toggleEditMode,
    updateDraftAddress,
    wasDraftSubmitted,
  };
}
